/**
 * S3 Content Hook
 * Fetches S3 folder listings and file contents via backend API
 */

import { useState, useCallback } from 'react';
import { useAuthenticatedFetch } from '../api/client';

export interface TranscriptionSegment {
  id: string;
  title: string;
  summary: string;
  transcript: string;
  startTime?: string;
  endTime?: string;
}

export interface SessionContent {
  masterSummary: string;
  segments: TranscriptionSegment[];
  audioUrl: string | null;
  metadata: {
    streamerName: string;
    vodDate: string;
  };
}

export interface UseS3ContentResult {
  // Folder listing
  sources: string[];
  sessions: string[];
  titles: string[];
  isLoadingSources: boolean;
  isLoadingSessions: boolean;
  isLoadingTitles: boolean;

  // Session content
  sessionContent: SessionContent | null;
  isLoadingSessionContent: boolean;
  sessionContentError: string | null;

  // Actions
  loadSources: () => Promise<void>;
  loadSessions: (source: string) => Promise<void>;
  loadTitles: (source: string, session: string) => Promise<void>;
  loadSessionContent: (source: string, session: string, title: string) => Promise<void>;
  /** Load content directly from a known s3_storage_location path, skipping S3 folder discovery */
  loadContentFromPath: (s3Path: string) => Promise<void>;
  reset: () => void;
}

interface S3ListResponse {
  folders?: string[];
  files?: string[];
}

interface S3ContentResponse {
  content?: string;
}

interface S3DownloadUrlResponse {
  download_url?: string;
}

/**
 * Hook for browsing and fetching S3 transcription content
 */
export function useS3Content(): UseS3ContentResult {
  const { fetchData } = useAuthenticatedFetch<S3ListResponse | S3ContentResponse | S3DownloadUrlResponse>();

  // Folder listing state
  const [sources, setSources] = useState<string[]>([]);
  const [sessions, setSessions] = useState<string[]>([]);
  const [titles, setTitles] = useState<string[]>([]);
  const [isLoadingSources, setIsLoadingSources] = useState(false);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [isLoadingTitles, setIsLoadingTitles] = useState(false);

  // Session content state
  const [sessionContent, setSessionContent] = useState<SessionContent | null>(null);
  const [isLoadingSessionContent, setIsLoadingSessionContent] = useState(false);
  const [sessionContentError, setSessionContentError] = useState<string | null>(null);

  // Load top-level sources (streamers)
  const loadSources = useCallback(async () => {
    setIsLoadingSources(true);
    try {
      const response = await fetchData('/echo/s3-list?prefix=') as S3ListResponse;
      setSources(response?.folders || []);
    } catch (err) {
      console.error('Failed to load sources:', err);
      setSources([]);
    } finally {
      setIsLoadingSources(false);
    }
  }, [fetchData]);

  // Load sessions for a source
  const loadSessions = useCallback(async (source: string) => {
    setIsLoadingSessions(true);
    setSessions([]);
    setTitles([]);
    try {
      const response = await fetchData(
        `/echo/s3-list?prefix=${encodeURIComponent(source + '/')}`
      ) as S3ListResponse;
      setSessions(response?.folders || []);
    } catch (err) {
      console.error('Failed to load sessions:', err);
      setSessions([]);
    } finally {
      setIsLoadingSessions(false);
    }
  }, [fetchData]);

  // Known content-type folders that are NOT title subfolders
  const CONTENT_FOLDERS = new Set(['summaries', 'transcriptions', 'segments', 'audio']);

  // Load titles for a session
  const loadTitles = useCallback(async (source: string, session: string) => {
    setIsLoadingTitles(true);
    setTitles([]);
    try {
      const response = await fetchData(
        `/echo/s3-list?prefix=${encodeURIComponent(source + '/' + session + '/')}`
      ) as S3ListResponse;

      const folders = response?.folders || [];
      const files = response?.files || [];

      // Check if subfolders are actual title folders or just content-type folders
      const titleFolders = folders.filter((f) => !CONTENT_FOLDERS.has(f.toLowerCase()));

      if (titleFolders.length > 0) {
        // Real title subfolders exist (e.g., streamer/date/vod-title/)
        setTitles(titleFolders);
      } else if (folders.length > 0 || files.length > 0) {
        // Only content folders (summaries/, transcriptions/) or files exist
        // directly in the session folder — treat as direct content
        setTitles(['[Direct Content]']);
      } else {
        setTitles([]);
      }
    } catch (err) {
      console.error('Failed to load titles:', err);
      setTitles([]);
    } finally {
      setIsLoadingTitles(false);
    }
  }, [fetchData]);

  // Load all content for a session (audio, summary, segments)
  const loadSessionContent = useCallback(async (source: string, session: string, title: string) => {
    setIsLoadingSessionContent(true);
    setSessionContentError(null);
    setSessionContent(null);

    try {
      // Use the title to construct the base path
      // If title is '[Direct Content]', the content is directly in the session folder
      const basePath = title === '[Direct Content]'
        ? `${source}/${session}/`
        : `${source}/${session}/${title}/`;
      const response = await fetchData(
        `/echo/s3-list?prefix=${encodeURIComponent(basePath)}`
      ) as S3ListResponse;

      const folders = response?.folders || [];
      const files = response?.files || [];

      // Find audio file
      let audioUrl: string | null = null;
      const audioFile = files.find(f => f.endsWith('.mp3'));
      if (audioFile) {
        try {
          const audioKey = `${basePath}${audioFile}`;
          const urlResponse = await fetchData(
            `/echo/s3-download-url?key=${encodeURIComponent(audioKey)}`
          ) as S3DownloadUrlResponse;
          audioUrl = urlResponse?.download_url || null;
        } catch {
          console.warn('Failed to get audio URL');
        }
      }

      // Load master summary
      let masterSummary = '';

      // Try master_summary.txt first
      try {
        const summaryResponse = await fetchData(
          `/echo/s3-content?key=${encodeURIComponent(basePath + 'master_summary.txt')}`
        ) as S3ContentResponse;
        masterSummary = summaryResponse?.content || '';
      } catch {
        // Try summaries folder
        if (folders.includes('summaries')) {
          try {
            const summariesList = await fetchData(
              `/echo/s3-list?prefix=${encodeURIComponent(basePath + 'summaries/')}`
            ) as S3ListResponse;
            const summaryFiles = summariesList?.files || [];

            // Look for master or overall summary
            const masterFile = summaryFiles.find(f =>
              f.includes('master') || f.includes('overall') || f.includes('final')
            ) || summaryFiles[0];

            if (masterFile) {
              const summaryResponse = await fetchData(
                `/echo/s3-content?key=${encodeURIComponent(basePath + 'summaries/' + masterFile)}`
              ) as S3ContentResponse;
              masterSummary = summaryResponse?.content || '';
            }
          } catch {
            console.warn('Failed to load summaries');
          }
        }
      }

      // Load segments from summaries and transcriptions folders
      const segments: TranscriptionSegment[] = [];

      if (folders.includes('summaries') || folders.includes('transcriptions')) {
        // Get list of summary files
        let summaryFiles: string[] = [];
        if (folders.includes('summaries')) {
          try {
            const summariesList = await fetchData(
              `/echo/s3-list?prefix=${encodeURIComponent(basePath + 'summaries/')}`
            ) as S3ListResponse;
            summaryFiles = (summariesList?.files || []).filter(f => f.endsWith('.txt'));
          } catch {
            // Ignore
          }
        }

        // Get list of transcription files
        let transcriptFiles: string[] = [];
        if (folders.includes('transcriptions')) {
          try {
            const transcriptList = await fetchData(
              `/echo/s3-list?prefix=${encodeURIComponent(basePath + 'transcriptions/')}`
            ) as S3ListResponse;
            transcriptFiles = (transcriptList?.files || []).filter(f => f.endsWith('.txt'));
          } catch {
            // Ignore
          }
        }

        // Helper to extract base segment name from filename
        const extractSegmentName = (filename: string): string => {
          return filename
            // Remove file extension
            .replace(/\.txt$/, '')
            // Remove common prefixes
            .replace(/^summary[_\s-]*/i, '')
            .replace(/^transcript[_\s-]*/i, '')
            // Remove common suffixes
            .replace(/[_\s-]*summary$/i, '')
            .replace(/[_\s-]*transcript$/i, '');
        };

        // Build a map of base segment names to their files
        const segmentMap = new Map<string, { summaryFile?: string; transcriptFile?: string }>();

        for (const file of summaryFiles) {
          const baseName = extractSegmentName(file);
          if (!segmentMap.has(baseName)) {
            segmentMap.set(baseName, {});
          }
          segmentMap.get(baseName)!.summaryFile = file;
        }

        for (const file of transcriptFiles) {
          const baseName = extractSegmentName(file);
          if (!segmentMap.has(baseName)) {
            segmentMap.set(baseName, {});
          }
          segmentMap.get(baseName)!.transcriptFile = file;
        }

        let segmentIndex = 0;
        for (const [segmentName, files] of Array.from(segmentMap.entries()).sort((a, b) => a[0].localeCompare(b[0]))) {
          // Skip master/overall summaries
          if (segmentName.includes('master') || segmentName.includes('overall') || segmentName.includes('final')) {
            continue;
          }

          let summary = '';
          let transcript = '';

          // Load summary
          if (files.summaryFile) {
            try {
              const resp = await fetchData(
                `/echo/s3-content?key=${encodeURIComponent(basePath + 'summaries/' + files.summaryFile)}`
              ) as S3ContentResponse;
              summary = resp?.content || '';
            } catch {
              // Ignore
            }
          }

          // Load transcript
          if (files.transcriptFile) {
            try {
              const resp = await fetchData(
                `/echo/s3-content?key=${encodeURIComponent(basePath + 'transcriptions/' + files.transcriptFile)}`
              ) as S3ContentResponse;
              transcript = resp?.content || '';
            } catch {
              // Ignore
            }
          }

          if (summary || transcript) {
            segments.push({
              id: `segment-${segmentIndex++}`,
              title: segmentName.replace(/_/g, ' ').replace(/segment/i, 'Segment '),
              summary,
              transcript,
            });
          }
        }
      }

      setSessionContent({
        masterSummary,
        segments,
        audioUrl,
        metadata: {
          streamerName: source,
          vodDate: session,
        },
      });
    } catch (err) {
      console.error('Failed to load session content:', err);
      setSessionContentError(err instanceof Error ? err.message : 'Failed to load session content');
    } finally {
      setIsLoadingSessionContent(false);
    }
  }, [fetchData]);

  // Load content directly from a known s3_storage_location path
  // Used by the DB-driven viewing page (useAccessibleRuns) to skip S3 folder discovery
  const loadContentFromPath = useCallback(async (s3Path: string) => {
    // Strip s3://bucket-name/ prefix to get relative key
    const relativePath = s3Path.replace(/^s3:\/\/[^/]+\//, '');
    const parts = relativePath.split('/').filter(Boolean);
    const source = parts[0] || '';
    const session = parts[1] || '';

    // Helper: attempt to list a prefix and load content from it
    const tryLoadFromPrefix = async (prefix: string): Promise<boolean> => {
      const response = await fetchData(
        `/echo/s3-list?prefix=${encodeURIComponent(prefix)}`
      ) as S3ListResponse;

      const folders = response?.folders || [];
      const files = response?.files || [];

      const titleFolders = folders.filter(
        (f) => !CONTENT_FOLDERS.has(f.toLowerCase())
      );

      const effectiveSource = prefix.split('/')[0];
      const effectiveSession = prefix.split('/')[1];

      if (titleFolders.length >= 1) {
        await loadSessionContent(effectiveSource, effectiveSession, titleFolders[0]);
        return true;
      } else if (folders.length > 0 || files.length > 0) {
        await loadSessionContent(effectiveSource, effectiveSession, '[Direct Content]');
        return true;
      }
      return false;
    };

    setIsLoadingSessionContent(true);
    setSessionContentError(null);

    try {
      // Try the source name as-is from the DB path
      const prefix = `${source}/${session}/`;
      const found = await tryLoadFromPrefix(prefix);

      if (!found) {
        // Fallback: slugify the source name in case old runs stored it raw
        const slugifiedSource = source.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
        if (slugifiedSource !== source) {
          const slugPrefix = `${slugifiedSource}/${session}/`;
          const foundSlug = await tryLoadFromPrefix(slugPrefix);
          if (!foundSlug) {
            setSessionContentError('No content found at this location');
            setIsLoadingSessionContent(false);
          }
        } else {
          setSessionContentError('No content found at this location');
          setIsLoadingSessionContent(false);
        }
      }
    } catch (err: unknown) {
      console.error('Failed to load content from path:', err);
      const errMsg = err && typeof err === 'object' && 'message' in err
        ? (err as { message: string }).message
        : JSON.stringify(err);
      setSessionContentError(`Failed to load content from "${source}/${session}/": ${errMsg}`);
      setIsLoadingSessionContent(false);
    }
  }, [fetchData, loadSessionContent]);

  // Reset all state
  const reset = useCallback(() => {
    setSources([]);
    setSessions([]);
    setTitles([]);
    setSessionContent(null);
    setSessionContentError(null);
  }, []);

  return {
    sources,
    sessions,
    titles,
    isLoadingSources,
    isLoadingSessions,
    isLoadingTitles,
    sessionContent,
    isLoadingSessionContent,
    sessionContentError,
    loadSources,
    loadSessions,
    loadTitles,
    loadSessionContent,
    loadContentFromPath,
    reset,
  };
}

export default useS3Content;
